const STATE = (window.SelecaoCustomizerState ??= new Map());
const MAX_DIMENSION = 4000;
const TEXT_BASE = 0.2;
const ACCEPTED_TYPES = /^image\/(jpeg|png|webp)$/;

const LAYOUTS = {
  1: [[0.5, 0.42, 0.9, 0.7]],
  2: [[0.26, 0.42, 0.46, 0.7], [0.74, 0.42, 0.46, 0.7]],
  3: [[0.5, 0.23, 0.6, 0.38], [0.26, 0.62, 0.46, 0.36], [0.74, 0.62, 0.46, 0.36]],
  4: [[0.26, 0.23, 0.46, 0.36], [0.74, 0.23, 0.46, 0.36], [0.26, 0.62, 0.46, 0.36], [0.74, 0.62, 0.46, 0.36]],
  5: [[0.5, 0.15, 0.46, 0.24], [0.26, 0.41, 0.46, 0.24], [0.74, 0.41, 0.46, 0.24], [0.26, 0.67, 0.46, 0.24], [0.74, 0.67, 0.46, 0.24]],
};
const TOUCH_TOLERANCE_CSS_PX = 20;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const round = (value) => Math.round(value * 10000) / 10000;

function loadImage(file) {
  const img = new Image();
  img.decoding = 'async';
  img.src = URL.createObjectURL(file);
  return img.decode().then(() => img);
}

function canvasToFile(canvas, name, quality = 0.92) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(new File([blob], name, { type: 'image/jpeg' })) : reject(new Error('toBlob failed'))),
      'image/jpeg',
      quality
    );
  });
}

async function downscale(img, fileName) {
  const ratio = Math.min(1, MAX_DIMENSION / Math.max(img.naturalWidth, img.naturalHeight));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(img.naturalWidth * ratio);
  canvas.height = Math.round(img.naturalHeight * ratio);
  canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvasToFile(canvas, `${fileName.replace(/\.[^.]+$/, '')}.jpg`);
}

function createState(color = '#FFFFFF') {
  return {
    photos: [],
    text: { value: '', font: 'script', color, x: 0.5, y: 0.5, scale: 0.8, rotation: 0 },
    selected: null,
    manual: false,
    saved: null,
  };
}

class ProductCustomizer extends HTMLElement {
  #onGuardedEvent = (event) => this.#guard(event);
  #onVariantChange = (event) => this.#watchVariant(event);

  connectedCallback() {
    const { dataset } = this;
    this.config = {
      formId: dataset.formId,
      maxPhotos: Number(dataset.maxPhotos) || 5,
      maxBytes: (Number(dataset.maxFileMb) || 10) * 1024 * 1024,
      require: dataset.require === 'true',
      printArea: {
        left: Number(dataset.printLeft) / 100,
        top: Number(dataset.printTop) / 100,
        width: Number(dataset.printWidth) / 100,
        height: Number(dataset.printHeight) / 100,
      },
    };

    this.state = STATE.get(dataset.productId) ?? createState(dataset.textColor);
    STATE.set(dataset.productId, this.state);

    this.dialog = this.querySelector('[data-dialog]');
    this.canvas = this.querySelector('[data-canvas]');
    this.ctx = this.canvas.getContext('2d');
    this.mockup = this.querySelector('[data-mockup]');
    this.fileInput = this.querySelector('[data-file-input]');
    this.drop = this.querySelector('[data-drop]');
    this.thumbs = this.querySelector('[data-thumbs]');
    this.error = this.querySelector('[data-error]');
    this.layerControls = this.querySelector('[data-layer-controls]');
    this.scaleInput = this.querySelector('[data-scale]');
    this.rotateInput = this.querySelector('[data-rotate]');
    this.textInput = this.querySelector('[data-text]');
    this.fields = this.querySelector('[data-fields]');
    this.mockups = JSON.parse(this.querySelector('[data-mockups]')?.textContent || '{}');
    this.zoomButton = this.querySelector('[data-zoom]');
    this.hint = this.querySelector('[data-hint]');
    this.compactQuery = matchMedia('(max-width: 749px)');
    this.coarseQuery = matchMedia('(pointer: coarse)');
    this.pointers = new Map();
    this.view = { x: 0, y: 0, s: 1 };

    this.#bindEvents();
    window.addEventListener('click', this.#onGuardedEvent, true);
    window.addEventListener('submit', this.#onGuardedEvent, true);
    document.addEventListener('change', this.#onVariantChange, true);

    if (this.textInput) this.textInput.value = this.state.text.value;
    this.#syncOptionButtons();
    if (this.state.saved) {
      this.#writeFields();
      this.#updateSummary();
    }
    this.#syncMockup();
  }

  disconnectedCallback() {
    window.removeEventListener('click', this.#onGuardedEvent, true);
    window.removeEventListener('submit', this.#onGuardedEvent, true);
    document.removeEventListener('change', this.#onVariantChange, true);
  }

  get #fonts() {
    return Object.fromEntries(
      [...this.querySelectorAll('[data-font]')].map((button) => [
        button.dataset.font,
        { family: button.dataset.family.replace(/^["']|["']$/g, ''), weight: button.dataset.weight, label: button.dataset.label },
      ])
    );
  }

  #bindEvents() {
    this.querySelector('[data-open]').addEventListener('click', () => this.open());
    this.querySelector('[data-close]').addEventListener('click', () => this.close());
    this.querySelector('[data-save]').addEventListener('click', () => this.#save());
    this.querySelector('[data-reset]').addEventListener('click', () => this.#reset());
    this.querySelector('[data-front]').addEventListener('click', () => this.#bringToFront());
    this.querySelector('[data-remove]').addEventListener('click', () => this.#removeSelected());
    this.zoomButton?.addEventListener('click', () => {
      this.state.zoom = !this.#isZoomed();
      this.#updateView();
      this.render();
    });
    this.compactQuery.addEventListener('change', () => {
      if (!this.dialog.open) return;
      this.#updateView();
      this.render();
    });

    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('close', () => document.documentElement.classList.remove('customizer-open'));

    this.fileInput.addEventListener('change', () => {
      this.#addFiles(this.fileInput.files);
      this.fileInput.value = '';
    });
    this.drop.addEventListener('dragover', (event) => {
      event.preventDefault();
      this.drop.classList.add('is-dragover');
    });
    this.drop.addEventListener('dragleave', () => this.drop.classList.remove('is-dragover'));
    this.drop.addEventListener('drop', (event) => {
      event.preventDefault();
      this.drop.classList.remove('is-dragover');
      this.#addFiles(event.dataTransfer.files);
    });

    this.canvas.addEventListener('pointerdown', (event) => this.#pointerDown(event));
    this.canvas.addEventListener('pointermove', (event) => this.#pointerMove(event));
    this.canvas.addEventListener('pointerup', (event) => this.#pointerUp(event));
    this.canvas.addEventListener('pointercancel', (event) => this.#pointerUp(event));
    this.canvas.addEventListener('wheel', (event) => this.#wheel(event), { passive: false });
    this.canvas.addEventListener('keydown', (event) => this.#keydown(event));

    this.scaleInput.addEventListener('input', () => {
      const layer = this.#selectedLayer();
      if (!layer) return;
      layer.scale = Number(this.scaleInput.value) / 100;
      this.state.manual = true;
      this.render();
    });
    this.rotateInput.addEventListener('input', () => {
      const layer = this.#selectedLayer();
      if (!layer) return;
      layer.rotation = Number(this.rotateInput.value);
      this.state.manual = true;
      this.render();
    });

    this.textInput?.addEventListener('input', () => {
      const { text, photos } = this.state;
      const wasEmpty = !text.value.trim();
      text.value = this.textInput.value;
      if (wasEmpty && text.value.trim() && !this.state.manual) {
        text.x = 0.5;
        text.y = photos.length ? 0.9 : 0.5;
      }
      this.#fitText({ reset: !this.state.manual });
      this.state.selected = text.value.trim() ? 'text' : null;
      this.#setError('');
      this.#updateCounts();
      this.#updateLayerControls();
      this.render();
    });

    for (const button of this.querySelectorAll('[data-font]')) {
      button.addEventListener('click', async () => {
        this.state.text.font = button.dataset.font;
        this.#syncOptionButtons();
        await this.#loadFonts();
        this.#fitText({ reset: !this.state.manual });
        this.#updateLayerControls();
        this.render();
      });
    }
    for (const button of this.querySelectorAll('[data-color]')) {
      button.addEventListener('click', () => {
        this.state.text.color = button.dataset.color;
        this.state.text.colorPicked = true;
        this.#syncOptionButtons();
        this.render();
      });
    }
  }

  async open() {
    this.querySelector('[data-notice]').hidden = true;
    if (!this.dialog.open) this.dialog.showModal();
    document.documentElement.classList.add('customizer-open');
    await this.#syncMockup();
    await this.#loadFonts();
    if (this.hint) this.hint.textContent = this.coarseQuery.matches ? this.hint.dataset.touch : this.hint.dataset.mouse;
    this.#updateView();
    this.#fitText();
    this.#renderThumbs();
    this.#updateCounts();
    this.#updateLayerControls();
    this.render();
  }

  #isZoomed() {
    return this.state.zoom ?? this.compactQuery.matches;
  }

  #updateView() {
    const size = this.canvas.width;
    const area = this.#printArea();
    const zoomed = this.#isZoomed();
    if (zoomed) {
      const region = Math.min(size, Math.max(area.w, area.h) * 1.35);
      this.view = {
        x: clamp(area.x + area.w / 2 - region / 2, 0, size - region),
        y: clamp(area.y + area.h / 2 - region / 2, 0, size - region),
        s: size / region,
      };
    } else {
      this.view = { x: 0, y: 0, s: 1 };
    }
    if (this.zoomButton) {
      this.zoomButton.setAttribute('aria-pressed', String(zoomed));
      this.zoomButton.textContent = zoomed ? this.zoomButton.dataset.labelOut : this.zoomButton.dataset.labelIn;
    }
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }

  #loadFonts() {
    if (!document.fonts) return Promise.resolve();
    return Promise.all(
      Object.values(this.#fonts).map(({ family, weight }) => document.fonts.load(`${weight} 64px "${family}"`).catch(() => {}))
    );
  }

  #printArea() {
    const size = this.canvas.width;
    const { left, top, width, height } = this.config.printArea;
    return { x: left * size, y: top * size, w: width * size, h: height * size };
  }

  #selectedLayer() {
    const { selected, photos, text } = this.state;
    if (selected === 'text') return text.value.trim() ? text : null;
    return photos.find((photo) => photo.id === selected) ?? null;
  }

  #textFont(scale, area) {
    const font = this.#fonts[this.state.text.font] ?? Object.values(this.#fonts)[0];
    const px = Math.max(8, scale * TEXT_BASE * area.w);
    return { css: `${font.weight} ${px}px "${font.family}", sans-serif`, px };
  }

  #box(layer, area) {
    const cx = area.x + layer.x * area.w;
    const cy = area.y + layer.y * area.h;
    const rotation = (layer.rotation * Math.PI) / 180;
    if (layer === this.state.text) {
      const { css, px } = this.#textFont(layer.scale, area);
      this.ctx.font = css;
      return { cx, cy, rotation, w: this.ctx.measureText(layer.value).width, h: px * 1.25 };
    }
    const w = layer.scale * area.w;
    return { cx, cy, rotation, w, h: w * (layer.img.naturalHeight / layer.img.naturalWidth) };
  }

  render({ guides = true, full = false } = {}) {
    const { ctx, canvas } = this;
    const size = canvas.width;
    const area = this.#printArea();
    const view = full ? { x: 0, y: 0, s: 1 } : this.view;
    const unit = 1 / view.s;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, size, size);
    ctx.setTransform(view.s, 0, 0, view.s, -view.x * view.s, -view.y * view.s);

    if (this.mockup.naturalWidth) {
      const scale = Math.max(size / this.mockup.naturalWidth, size / this.mockup.naturalHeight);
      const w = this.mockup.naturalWidth * scale;
      const h = this.mockup.naturalHeight * scale;
      ctx.drawImage(this.mockup, (size - w) / 2, (size - h) / 2, w, h);
    }

    if (guides) {
      ctx.save();
      ctx.lineWidth = 3 * unit;
      ctx.setLineDash([12 * unit, 12 * unit]);
      ctx.strokeStyle = 'rgba(31, 59, 51, 0.6)';
      ctx.strokeRect(area.x, area.y, area.w, area.h);
      ctx.lineDashOffset = 12 * unit;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.strokeRect(area.x, area.y, area.w, area.h);
      ctx.restore();
    }

    ctx.save();
    ctx.beginPath();
    ctx.rect(area.x, area.y, area.w, area.h);
    ctx.clip();

    for (const photo of this.state.photos) {
      const box = this.#box(photo, area);
      ctx.save();
      ctx.translate(box.cx, box.cy);
      ctx.rotate(box.rotation);
      ctx.drawImage(photo.img, -box.w / 2, -box.h / 2, box.w, box.h);
      ctx.restore();
    }

    const { text } = this.state;
    if (text.value.trim()) {
      const box = this.#box(text, area);
      ctx.save();
      ctx.translate(box.cx, box.cy);
      ctx.rotate(box.rotation);
      ctx.font = this.#textFont(text.scale, area).css;
      ctx.fillStyle = text.color;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text.value, 0, 0);
      ctx.restore();
    }
    ctx.restore();

    const selected = guides && this.#selectedLayer();
    if (selected) {
      const box = this.#box(selected, area);
      ctx.save();
      ctx.translate(box.cx, box.cy);
      ctx.rotate(box.rotation);
      const pad = 6 * unit;
      const handle = 18 * unit;
      const rect = [-box.w / 2 - pad, -box.h / 2 - pad, box.w + pad * 2, box.h + pad * 2];
      ctx.lineWidth = 8 * unit;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
      ctx.strokeRect(...rect);
      ctx.lineWidth = 4 * unit;
      ctx.strokeStyle = '#C4603F';
      ctx.strokeRect(...rect);
      for (const [x, y] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
        const hx = (x * (box.w + pad * 2)) / 2 - handle / 2;
        const hy = (y * (box.h + pad * 2)) / 2 - handle / 2;
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(hx - 2 * unit, hy - 2 * unit, handle + 4 * unit, handle + 4 * unit);
        ctx.fillStyle = '#C4603F';
        ctx.fillRect(hx, hy, handle, handle);
      }
      ctx.restore();
    }
    ctx.setTransform(1, 0, 0, 1, 0, 0);
  }

  #canvasPoint(event) {
    const rect = this.canvas.getBoundingClientRect();
    const x = ((event.clientX - rect.left) * this.canvas.width) / rect.width;
    const y = ((event.clientY - rect.top) * this.canvas.height) / rect.height;
    return { x: x / this.view.s + this.view.x, y: y / this.view.s + this.view.y };
  }

  #touchTolerance() {
    const rect = this.canvas.getBoundingClientRect();
    const canvasPerCssPx = this.canvas.width / (rect.width || this.canvas.width);
    return (TOUCH_TOLERANCE_CSS_PX * canvasPerCssPx) / this.view.s;
  }

  #hitTest(point) {
    const area = this.#printArea();
    const { text, photos } = this.state;
    const layers = [...photos];
    if (text.value.trim()) layers.push(text);
    const tolerance = this.#touchTolerance();
    for (const layer of layers.reverse()) {
      const box = this.#box(layer, area);
      const dx = point.x - box.cx;
      const dy = point.y - box.cy;
      const lx = dx * Math.cos(-box.rotation) - dy * Math.sin(-box.rotation);
      const ly = dx * Math.sin(-box.rotation) + dy * Math.cos(-box.rotation);
      if (Math.abs(lx) <= box.w / 2 + tolerance && Math.abs(ly) <= box.h / 2 + tolerance) return layer;
    }
    return null;
  }

  #pinchGeometry() {
    const [a, b] = [...this.pointers.values()];
    return { distance: Math.hypot(b.x - a.x, b.y - a.y), angle: Math.atan2(b.y - a.y, b.x - a.x) };
  }

  #pointerDown(event) {
    const point = this.#canvasPoint(event);
    this.pointers.set(event.pointerId, point);
    this.canvas.setPointerCapture(event.pointerId);

    if (this.pointers.size === 2) {
      const layer = this.#selectedLayer();
      this.drag = null;
      if (layer) this.pinch = { layer, scale: layer.scale, rotation: layer.rotation, ...this.#pinchGeometry() };
      return;
    }

    const layer = this.#hitTest(point);
    this.state.selected = layer === this.state.text ? 'text' : layer?.id ?? null;
    this.drag = layer ? { pointerId: event.pointerId, start: point, x: layer.x, y: layer.y, layer } : null;
    this.#renderThumbs();
    this.#updateLayerControls();
    this.render();
  }

  #pointerMove(event) {
    if (!this.pointers.has(event.pointerId)) return;
    const point = this.#canvasPoint(event);
    this.pointers.set(event.pointerId, point);

    if (this.pinch && this.pointers.size >= 2) {
      const { distance, angle } = this.#pinchGeometry();
      const { layer } = this.pinch;
      if (this.pinch.distance > 0) layer.scale = clamp(this.pinch.scale * (distance / this.pinch.distance), 0.1, 2);
      layer.rotation = clamp(Math.round(this.pinch.rotation + ((angle - this.pinch.angle) * 180) / Math.PI), -180, 180);
      this.state.manual = true;
      this.#updateLayerControls();
      this.render();
      return;
    }

    if (!this.drag || this.drag.pointerId !== event.pointerId) return;
    const area = this.#printArea();
    const { layer, start, x, y } = this.drag;
    layer.x = clamp(x + (point.x - start.x) / area.w, 0, 1);
    layer.y = clamp(y + (point.y - start.y) / area.h, 0, 1);
    this.state.manual = true;
    this.render();
  }

  #pointerUp(event) {
    this.pointers.delete(event.pointerId);
    if (this.pointers.size < 2) this.pinch = null;
    if (this.drag?.pointerId === event.pointerId) this.drag = null;
  }

  #wheel(event) {
    const layer = this.#selectedLayer();
    if (!layer) return;
    event.preventDefault();
    layer.scale = clamp(layer.scale * (event.deltaY < 0 ? 1.05 : 0.95), 0.1, 2);
    this.state.manual = true;
    this.#updateLayerControls();
    this.render();
  }

  #keydown(event) {
    const layer = this.#selectedLayer();
    if (!layer) return;
    const step = event.shiftKey ? 0.05 : 0.01;
    const moves = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
    if (moves[event.key]) {
      event.preventDefault();
      layer.x = clamp(layer.x + moves[event.key][0], 0, 1);
      layer.y = clamp(layer.y + moves[event.key][1], 0, 1);
      this.state.manual = true;
      this.render();
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      this.#removeSelected();
    }
  }

  #fitText({ reset = false } = {}) {
    const { text } = this.state;
    if (!text.value.trim()) return;
    const area = this.#printArea();
    if (reset) text.scale = 0.8;
    const { w } = this.#box(text, area);
    const maxWidth = area.w * 0.9;
    if (w > maxWidth) text.scale = Math.max(0.1, (text.scale * maxWidth) / w);
  }

  #applyLayout() {
    const { photos } = this.state;
    const area = this.#printArea();
    const layout = LAYOUTS[photos.length] ?? [];
    photos.forEach((photo, index) => {
      const [x, y, maxW, maxH] = layout[index];
      const aspect = photo.img.naturalWidth / photo.img.naturalHeight;
      photo.x = x;
      photo.y = y;
      photo.rotation = 0;
      photo.scale = Math.min(maxW * area.w, maxH * area.h * aspect) / area.w;
    });
    const { text } = this.state;
    text.x = 0.5;
    text.y = photos.length ? 0.9 : 0.5;
    text.rotation = 0;
    this.#fitText({ reset: true });
  }

  async #addFiles(fileList) {
    this.#setError('');
    const { photos } = this.state;
    const room = this.config.maxPhotos - photos.length;
    const files = [...fileList];
    if (!files.length) return;
    if (files.length > room) this.#setError(this.dataset.errorMax);

    let added = null;
    for (const file of files.slice(0, Math.max(0, room))) {
      if (!ACCEPTED_TYPES.test(file.type)) {
        this.#setError(this.dataset.errorType);
        continue;
      }
      try {
        let img = await loadImage(file);
        let upload = file;
        if (file.size > this.config.maxBytes || Math.max(img.naturalWidth, img.naturalHeight) > MAX_DIMENSION) {
          upload = await downscale(img, file.name);
          if (upload.size > this.config.maxBytes) {
            this.#setError(this.dataset.errorSize);
            continue;
          }
        }
        const photo = { id: crypto.randomUUID(), file: upload, img, x: 0.5, y: 0.5, scale: 0.5, rotation: 0 };
        photos.push(photo);
        added = photo;
      } catch {
        this.#setError(this.dataset.errorType);
      }
    }

    if (added) {
      if (!this.state.manual) this.#applyLayout();
      this.state.selected = added.id;
    }
    this.#renderThumbs();
    this.#updateCounts();
    this.#updateLayerControls();
    this.render();
  }

  #removeSelected() {
    const { selected, photos, text } = this.state;
    if (selected === 'text') {
      text.value = '';
      if (this.textInput) this.textInput.value = '';
    } else {
      const index = photos.findIndex((photo) => photo.id === selected);
      if (index === -1) return;
      URL.revokeObjectURL(photos[index].img.src);
      photos.splice(index, 1);
      if (!this.state.manual) this.#applyLayout();
    }
    this.state.selected = null;
    this.#renderThumbs();
    this.#updateCounts();
    this.#updateLayerControls();
    this.render();
  }

  #bringToFront() {
    const { photos, selected } = this.state;
    const index = photos.findIndex((photo) => photo.id === selected);
    if (index === -1) return;
    photos.push(...photos.splice(index, 1));
    this.state.manual = true;
    this.#renderThumbs();
    this.render();
  }

  #reset() {
    this.state.manual = false;
    this.#applyLayout();
    this.#updateLayerControls();
    this.render();
  }

  #renderThumbs() {
    const { photos, selected } = this.state;
    this.thumbs.replaceChildren(
      ...photos.map((photo, index) => {
        const item = document.createElement('li');
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-customizer__thumb-button';
        button.setAttribute('aria-pressed', String(photo.id === selected));
        button.setAttribute('aria-label', `${this.dataset.labelPhotoName} ${index + 1}`);
        const img = document.createElement('img');
        img.src = photo.img.src;
        img.alt = '';
        const badge = document.createElement('span');
        badge.className = 'product-customizer__thumb-index';
        badge.textContent = String(index + 1);
        button.append(img, badge);
        button.addEventListener('click', () => {
          this.state.selected = photo.id;
          this.#renderThumbs();
          this.#updateLayerControls();
          this.render();
        });
        item.append(button);
        return item;
      })
    );
  }

  #updateCounts() {
    const { photos, text } = this.state;
    this.querySelector('[data-photo-count]').textContent = `${photos.length}/${this.config.maxPhotos}`;
    this.drop.classList.toggle('is-full', photos.length >= this.config.maxPhotos);
    const textCount = this.querySelector('[data-text-count]');
    if (textCount && this.textInput) textCount.textContent = `${text.value.length}/${this.textInput.maxLength}`;
  }

  #updateLayerControls() {
    const layer = this.#selectedLayer();
    this.layerControls.hidden = !layer;
    if (!layer) return;
    const isText = layer === this.state.text;
    const index = this.state.photos.indexOf(layer);
    this.querySelector('[data-layer-name]').textContent = isText
      ? this.textInput?.getAttribute('aria-label') ?? ''
      : `${this.dataset.labelPhotoName} ${index + 1}`;
    this.querySelector('[data-front]').hidden = isText;
    this.scaleInput.value = String(Math.round(layer.scale * 100));
    this.rotateInput.value = String(Math.round(layer.rotation));
  }

  #syncOptionButtons() {
    const { font, color } = this.state.text;
    for (const button of this.querySelectorAll('[data-font]')) {
      button.setAttribute('aria-checked', String(button.dataset.font === font));
    }
    for (const button of this.querySelectorAll('[data-color]')) {
      button.setAttribute('aria-checked', String(button.dataset.color === color));
    }
  }

  #setError(message) {
    this.error.textContent = message;
    this.error.hidden = !message;
  }

  async #save() {
    if (await this.#commit()) this.close();
  }

  async #commit() {
    const { photos, text } = this.state;
    const hasText = Boolean(this.textInput && text.value.trim());
    if (!photos.length && !hasText) {
      this.#setError(this.dataset.errorEmpty);
      return false;
    }

    await this.#loadFonts();
    this.#fitText();
    this.render({ guides: false, full: true });
    const preview = await canvasToFile(this.canvas, 'design-preview.jpg', 0.9);
    this.render();

    if (this.state.saved?.previewUrl) URL.revokeObjectURL(this.state.saved.previewUrl);
    const area = this.config.printArea;
    const fonts = this.#fonts;
    const colorButton = this.querySelector(`[data-color="${text.color}"]`);

    this.state.saved = {
      preview,
      previewUrl: URL.createObjectURL(preview),
      files: photos.map((photo) => photo.file),
      text: hasText
        ? { value: text.value.trim(), font: fonts[text.font]?.label ?? text.font, color: colorButton?.dataset.label ?? text.color }
        : null,
      design: {
        printArea: area,
        photos: photos.map((photo, index) => ({
          name: `Photo ${index + 1}`,
          x: round(photo.x),
          y: round(photo.y),
          scale: round(photo.scale),
          rotation: Math.round(photo.rotation),
        })),
        text: hasText
          ? { value: text.value.trim(), font: text.font, color: text.color, x: round(text.x), y: round(text.y), scale: round(text.scale), rotation: Math.round(text.rotation) }
          : null,
      },
    };

    this.#writeFields();
    this.#updateSummary();
    return true;
  }

  #currentVariantId() {
    return document.getElementById(this.config.formId)?.querySelector('input[name="id"]')?.value ?? '';
  }

  #watchVariant(event) {
    if (!(event.target instanceof Element) || !event.target.closest('variant-picker, [data-variant-picker], .variant-picker')) return;
    const before = this.#currentVariantId();
    let attempts = 0;
    const poll = () => {
      if (!this.isConnected) return;
      if (this.#currentVariantId() !== before || attempts++ > 40) {
        this.#syncMockup();
        return;
      }
      setTimeout(poll, 100);
    };
    setTimeout(poll, 100);
  }

  async #syncMockup() {
    const url = this.mockups[this.#currentVariantId()];
    if (url && this.mockup.dataset.url !== url) {
      this.mockup.dataset.url = url;
      this.mockup.src = url;
    }
    if (!this.mockup.complete || !this.mockup.naturalWidth) await this.mockup.decode().catch(() => {});
    if (this.mockup.dataset.appliedUrl === this.mockup.dataset.url) return;
    this.mockup.dataset.appliedUrl = this.mockup.dataset.url;
    this.#autoTextColor();
    this.render();
    if (this.state.saved) await this.#commit();
  }

  #autoTextColor() {
    if (this.state.text.colorPicked || !this.mockup.naturalWidth) return;
    const { left, top, width, height } = this.config.printArea;
    const probe = document.createElement('canvas');
    probe.width = 24;
    probe.height = 24;
    const context = probe.getContext('2d', { willReadFrequently: true });
    const w = this.mockup.naturalWidth;
    const h = this.mockup.naturalHeight;
    context.drawImage(this.mockup, left * w, top * h, width * w, height * h, 0, 0, 24, 24);
    const { data } = context.getImageData(0, 0, 24, 24);
    let luminance = 0;
    for (let i = 0; i < data.length; i += 4) {
      luminance += (0.2126 * data[i] + 0.7152 * data[i + 1] + 0.0722 * data[i + 2]) / 255;
    }
    luminance /= data.length / 4;
    this.state.text.color = luminance < 0.55 ? '#FFFFFF' : '#1F3B33';
    this.#syncOptionButtons();
  }

  #writeFields() {
    const { saved } = this.state;
    const formId = this.config.formId;
    const inputs = [];

    const fileInput = (name, file) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.name = `properties[${name}]`;
      input.setAttribute('form', formId);
      const transfer = new DataTransfer();
      transfer.items.add(file);
      input.files = transfer.files;
      return input;
    };
    const hiddenInput = (name, value) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      input.value = value;
      input.setAttribute('form', formId);
      return input;
    };

    saved.files.forEach((file, index) => inputs.push(fileInput(`Photo ${index + 1}`, file)));
    if (saved.text) {
      inputs.push(hiddenInput('Text', saved.text.value), hiddenInput('Font', saved.text.font), hiddenInput('Text color', saved.text.color));
    }
    inputs.push(fileInput('Preview', saved.preview), hiddenInput('_Design', JSON.stringify(saved.design)));
    this.fields.replaceChildren(...inputs);
  }

  #updateSummary() {
    const { saved } = this.state;
    const preview = this.querySelector('[data-summary-preview]');
    preview.src = saved.previewUrl;
    preview.hidden = false;

    const count = saved.files.length;
    const parts = [];
    if (count) parts.push(`${count} ${count === 1 ? this.dataset.labelPhoto : this.dataset.labelPhotos}`);
    if (saved.text) parts.push(`“${saved.text.value}”`);
    this.querySelector('[data-summary-text]').textContent = parts.join(' · ');
    this.querySelector('[data-open-label]').textContent = this.dataset.labelEdit;
    this.querySelector('[data-notice]').hidden = true;
  }

  #guard(event) {
    if (!this.config.require || this.state.saved || !this.isConnected) return;
    const form =
      event.type === 'submit'
        ? event.target
        : event.target instanceof Element && event.target.closest('button[type="submit"], input[type="submit"]')?.form;
    if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.config.formId) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.open().then(() => {
      this.#setError(this.dataset.errorEmpty);
    });
    this.querySelector('[data-notice]').hidden = false;
  }
}

if (!customElements.get('product-customizer')) {
  customElements.define('product-customizer', ProductCustomizer);
}
